import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Stack,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Groups,
  People,
  CardMembership,
  EventAvailable,
  Add as AddIcon,
  ArrowForward,
  Logout as CheckOutIcon,
} from "@mui/icons-material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { useHomePage } from "@/hooks";
import { HOME_STAT_COLORS } from "@/constants";
import { EMPTY_CELL_PLACEHOLDER } from "@/constants/display";
import PageLoadingState from "@/components/PageLoadingState";
import { StatCard } from "@/components/home";

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    clubs,
    members,
    memberships,
    attendances,
    loading,
    error,
    checkInOpen,
    setCheckInOpen,
    selectedMember,
    setSelectedMember,
    creating,
    checkingOut,
    handleQuickCheckIn,
    handleQuickCheckOut,
    memberMap,
    recentAttendances,
    formatDateTime,
    closeCheckInDialog,
  } = useHomePage();

  if (loading) return <PageLoadingState />;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 3,
        }}
      >
        {t("nav.home")}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title={t("nav.clubs")}
          count={clubs?.length}
          icon={<Groups />}
          color={HOME_STAT_COLORS.clubs}
          emptyLabel={EMPTY_CELL_PLACEHOLDER}
          onClick={() => navigate("/clubs")}
        />
        <StatCard
          title={t("nav.members")}
          count={members?.length}
          icon={<People />}
          color={HOME_STAT_COLORS.members}
          emptyLabel={EMPTY_CELL_PLACEHOLDER}
          onClick={() => navigate("/members")}
        />
        <StatCard
          title={t("nav.memberships")}
          count={memberships?.length}
          icon={<CardMembership />}
          color={HOME_STAT_COLORS.memberships}
          emptyLabel={EMPTY_CELL_PLACEHOLDER}
          onClick={() => navigate("/memberships")}
        />
        <StatCard
          title={t("nav.attendances")}
          count={attendances?.length}
          icon={<EventAvailable />}
          color={HOME_STAT_COLORS.attendances}
          emptyLabel={EMPTY_CELL_PLACEHOLDER}
          onClick={() => navigate("/attendances")}
        />
      </Box>
      <Card>
        <CardContent sx={{ pb: "16px !important" }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {t("home.recentAttendances")}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCheckInOpen(true)}
              >
                {t("home.quickCheckIn")}
              </Button>
              <Tooltip title={t("home.viewAll")}>
                <IconButton
                  size="small"
                  onClick={() => navigate("/attendances")}
                >
                  <ArrowForward fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {recentAttendances.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                py: 3,
              }}
            >
              {t("home.noAttendances")}
            </Typography>
          ) : (
            <List disablePadding>
              {recentAttendances.map((a) => (
                <ListItem
                  key={a.id}
                  sx={{
                    px: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  secondaryAction={
                    a.endedAt ? (
                      <Chip
                        icon={<CheckCircle sx={{ fontSize: 16 }} />}
                        label={formatDateTime(a.endedAt)}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Tooltip title={t("home.quickCheckOut")}>
                        <Chip
                          icon={<CheckOutIcon sx={{ fontSize: 16 }} />}
                          label={t("home.quickCheckOut")}
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => handleQuickCheckOut(a.id)}
                          disabled={checkingOut}
                          sx={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                        color: "primary.main",
                        width: 36,
                        height: 36,
                      }}
                    >
                      <People sx={{ fontSize: 18 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={memberMap.get(a.memberId) ?? a.memberId}
                    secondary={formatDateTime(a.startedAt)}
                    slotProps={{
                      primary: {
                        sx: {
                          fontWeight: 500,
                          fontSize: "0.875rem",
                        },
                      },
                      secondary: { sx: { fontSize: "0.75rem" } },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={checkInOpen}
        onClose={closeCheckInDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("home.quickCheckIn")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("common.member")}
            select
            value={selectedMember}
            onChange={(e) => setSelectedMember(e.target.value)}
            fullWidth
            size="small"
            required
            sx={{ mt: 1 }}
          >
            <MenuItem value="">{EMPTY_CELL_PLACEHOLDER}</MenuItem>
            {(members ?? []).map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCheckInDialog}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleQuickCheckIn}
            disabled={creating || !selectedMember}
          >
            {t("home.quickCheckIn")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
